// Два транспорта под одним интерфейсом.
//
// WebSocket — основной: меньше задержка, отмена идёт тем же каналом.
// SSE — запасной, для сетей и прокси, которые режут апгрейд соединения.
// Кадры одинаковые, поэтому весь код выше по стеку про транспорт не знает.
//
// Оба умеют `resume`: генерация на сервере переживает разрыв связи, и после
// переподключения можно забрать пропущенное вместо повисшего индикатора.

import type { AttachmentRef, ClientEvent, ServerEvent } from './protocol'
import { getToken } from './api'

export type TransportKind = 'ws' | 'sse'

export interface SendOptions {
  convId: number | null
  text: string
  /** Идемпотентность: повтор отправки с тем же id не заведёт дубль. */
  clientId?: string
  /** Файлы из песочницы: картинки уйдут модели кадром, остальные — fs-тулу. */
  attachments?: AttachmentRef[]
  onEvent: (ev: ServerEvent) => void
}

export interface Transport {
  readonly kind: TransportKind
  /** Установить соединение, ничего не отправляя. Для проверки доступности. */
  open(): Promise<void>
  send(opts: SendOptions): Promise<void>
  /** Забрать генерацию, которая шла до разрыва. */
  resume(convId: number, onEvent: (ev: ServerEvent) => void): Promise<void>
  /** Прервать текущую генерацию. */
  cancel(): void
  /**
   * Связь пропала посреди ответа. Вызывающий решает, звать ли resume.
   * На завершённом потоке НЕ вызывается.
   */
  setOnDisconnect(cb: (() => void) | null): void
  close(): void
}

function authHeaders(): Record<string, string> {
  const token = getToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

// ── WebSocket ───────────────────────────────────────────────────────────────

class WsTransport implements Transport {
  readonly kind = 'ws' as const
  private socket: WebSocket | null = null
  private onEvent: ((ev: ServerEvent) => void) | null = null
  private keepAlive: ReturnType<typeof setInterval> | null = null
  private onDisconnect: (() => void) | null = null
  /** Идёт ли сейчас ответ: по этому решаем, звать ли onDisconnect. */
  private streaming = false
  private closedByUs = false

  setOnDisconnect(cb: (() => void) | null): void {
    this.onDisconnect = cb
  }

  async open(): Promise<void> {
    await this.connect()
  }

  private async connect(): Promise<WebSocket> {
    if (this.socket?.readyState === WebSocket.OPEN) return this.socket

    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:'
    const token = getToken()
    const url = `${proto}//${location.host}/api/ws`

    // Credentials must not be put in the URL: reverse proxies and browser
    // diagnostics routinely retain it. The server selects only `ozarnik`, so
    // the auth offer is not echoed back.
    const socket = token
      ? new WebSocket(url, ['ozarnik', `auth.${token}`])
      : new WebSocket(url, ['ozarnik'])
    this.socket = socket

    socket.onmessage = (e) => {
      try {
        const ev = JSON.parse(e.data) as ServerEvent
        if (
          ev.type === 'done' ||
          ev.type === 'cancelled' ||
          ev.type === 'error' ||
          ev.type === 'idle'
        ) {
          this.streaming = false
        }
        this.onEvent?.(ev)
      } catch {
        // Битый кадр — не роняем соединение, следующий может быть целым.
      }
    }
    socket.onclose = () => {
      if (this.keepAlive) clearInterval(this.keepAlive)
      this.keepAlive = null
      this.socket = null
      // Разрыв посреди ответа — повод вернуться к генерации.
      if (this.streaming && !this.closedByUs) {
        this.streaming = false
        this.onDisconnect?.()
      }
    }

    await new Promise<void>((resolve, reject) => {
      socket.onopen = () => resolve()
      socket.onerror = () => reject(new Error('websocket не открылся'))
    })

    // Прокси любят закрывать простаивающие сокеты.
    this.keepAlive = setInterval(() => {
      if (socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: 'ping' } satisfies ClientEvent))
      }
    }, 30_000)

    return socket
  }

  async send({ convId, text, clientId, attachments, onEvent }: SendOptions): Promise<void> {
    this.onEvent = onEvent
    const socket = await this.connect()
    this.streaming = true
    socket.send(
      JSON.stringify({
        type: 'user_msg',
        conv_id: convId,
        text,
        client_id: clientId,
        attachments,
      } satisfies ClientEvent),
    )
  }

  async resume(convId: number, onEvent: (ev: ServerEvent) => void): Promise<void> {
    this.onEvent = onEvent
    const socket = await this.connect()
    this.streaming = true
    socket.send(JSON.stringify({ type: 'resume', conv_id: convId } satisfies ClientEvent))
  }

  cancel(): void {
    this.streaming = false
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ type: 'cancel' } satisfies ClientEvent))
    }
  }

  close(): void {
    this.closedByUs = true
    if (this.keepAlive) clearInterval(this.keepAlive)
    this.keepAlive = null
    this.socket?.close()
    this.socket = null
  }
}

// ── SSE ─────────────────────────────────────────────────────────────────────

class SseTransport implements Transport {
  readonly kind = 'sse' as const
  private abort: AbortController | null = null
  private onDisconnect: (() => void) | null = null

  setOnDisconnect(cb: (() => void) | null): void {
    this.onDisconnect = cb
  }

  async open(): Promise<void> {
    // SSE-поток открывается вместе с отправкой сообщения, проверять нечего.
  }

  async send({ convId, text, clientId, attachments, onEvent }: SendOptions): Promise<void> {
    await this.stream(
      '/api/chat',
      { conv_id: convId, text, client_id: clientId, attachments },
      onEvent,
    )
  }

  async resume(convId: number, onEvent: (ev: ServerEvent) => void): Promise<void> {
    await this.stream('/api/resume', { conv_id: convId }, onEvent)
  }

  private async stream(
    url: string,
    body: unknown,
    onEvent: (ev: ServerEvent) => void,
  ): Promise<void> {
    this.abort?.abort()
    const abort = new AbortController()
    this.abort = abort

    let resp: Response
    try {
      resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body),
        signal: abort.signal,
      })
    } catch (e) {
      if (!abort.signal.aborted) {
        this.onDisconnect?.()
      }
      void e
      return
    }

    if (!resp.ok || !resp.body) {
      const detail = await resp.text().catch(() => '')
      let message = `сервер ответил ${resp.status}`
      try {
        const parsed = JSON.parse(detail)
        if (parsed?.error) message = parsed.error
      } catch {
        // тело не json — оставляем общий текст
      }
      onEvent({ type: 'error', message })
      return
    }

    // EventSource умеет только GET, поэтому поток читаем вручную.
    const reader = resp.body.pipeThrough(new TextDecoderStream()).getReader()
    let buffer = ''
    let sawTerminal = false
    try {
      for (;;) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += value
        // Кадры SSE разделены пустой строкой.
        let boundary: number
        while ((boundary = buffer.indexOf('\n\n')) !== -1) {
          const raw = buffer.slice(0, boundary)
          buffer = buffer.slice(boundary + 2)
          const payload = raw
            .split('\n')
            .filter((l) => l.startsWith('data:'))
            .map((l) => l.slice(5).trim())
            .join('')
          if (!payload) continue
          try {
            const ev = JSON.parse(payload) as ServerEvent
            if (
              ev.type === 'done' ||
              ev.type === 'cancelled' ||
              ev.type === 'error' ||
              ev.type === 'idle'
            ) {
              sawTerminal = true
            }
            onEvent(ev)
          } catch {
            // пропускаем нечитаемый кадр
          }
        }
      }
    } catch {
      // Отмена — это наш собственный abort, не ошибка.
    }

    // Поток кончился, а терминального кадра не было — связь оборвалась.
    if (!sawTerminal && !abort.signal.aborted) {
      this.onDisconnect?.()
    }
  }

  cancel(): void {
    // У SSE нет обратного канала: рвём соединение.
    // Сервер увидит закрытие, но генерацию не снимет — для этого есть
    // отдельный вызов отмены на уровне выше.
    this.abort?.abort()
    this.abort = null
  }

  close(): void {
    this.cancel()
  }
}

// ── выбор ───────────────────────────────────────────────────────────────────

/**
 * Пробует WebSocket, при неудаче откатывается на SSE.
 * `force` (?transport=sse) полезен, чтобы проверить запасной путь руками.
 */
export async function createTransport(force?: TransportKind): Promise<Transport> {
  if (force === 'sse') return new SseTransport()
  if (force === 'ws') return new WsTransport()

  const ws = new WsTransport()
  try {
    // Только рукопожатие: отправлять сюда user_msg нельзя, иначе проверка
    // доступности сама запустила бы генерацию с пустым текстом.
    await ws.open()
    return ws
  } catch {
    ws.close()
    return new SseTransport()
  }
}
